/**
 * Min Max Bet Component
 * Shows minimum and maximum bet amounts for games
 */

import React from 'react';
import type IGameSettings from '../../../../../../types/models/IGameSettings';
import { formatMinMaxBetsForDisplay, formatBetValue, SPJ_JACKPOT_GAMES } from '@repo/common';
import styles from './GameComponents.module.css';
import { useViewMode, useAssetsConfig } from '../../../../../../store/app/appStore';
import { MULTI_VIEW_MODE } from '../../../../../../constants';

interface MinMaxBetProps {
  data: IGameSettings;
  currency: string;
}

interface BetIconProps {
  betIconUrl: string | null;
}

const BetIcon = ({ betIconUrl }: BetIconProps) => (
  <div
    className={styles.betIcon}
    role="img"
    aria-label="Bet"
    style={{
      backgroundImage: betIconUrl ? `url(${betIconUrl})` : 'none'
    }}
  />
);

const MAX_BET_TEXT = 'MAX';

const MinMaxBet: React.FC<MinMaxBetProps> = ({ data, currency }) => {
  const minBet = data.minPossibleBets.get(currency) || 0;
  const maxBet = data.maxPossibleBets.get(currency) || 0;
  const viewMode = useViewMode();
  const isSmallScreen = window.innerWidth <= 390;
  const { assetsHost, assetsHash } = useAssetsConfig();

  // Construct BET icon URL using same logic as Image component
  const getBetIconUrl = () => {
    if (!assetsHost || assetsHost === '') return '';
    if (assetsHost.includes('cdn') && (!assetsHash || assetsHash === '')) return '';

    const rootPath = assetsHash && assetsHash.trim() !== '' ? `/${assetsHash}/assets/portal` : '/assets/portal';
    return `${assetsHost}${rootPath}/icons/BET.webp${assetsHash && assetsHash.trim() !== '' ? `?hash=${assetsHash}` : ''}`;
  };

  const betIconUrl = getBetIconUrl();
  
  // Don't show if no bet data available
  if (!minBet && !maxBet) {
    return null;
  }
  
  // Check if this is a single progressive jackpot game (shows only max bet)
  const isSPJGame = SPJ_JACKPOT_GAMES.includes(data.jackpotName ?? '');
  
  if (isSPJGame) {
    // Single Progressive Game - show only max bet (like old lobby)
    return (
      <div className={`${styles.minMaxContainer} min-max-bet`}>
        <BetIcon betIconUrl={betIconUrl} />
        <span className={`${styles.maxBetText} max-bet-text`}>{formatBetValue(maxBet)}</span>
        <span className={`${styles.maxBetTextSPJ}`}>{MAX_BET_TEXT}</span>
      </div>
    );
  }

  // Default behavior - show min - max format (like old lobby)
  const isMultiView = viewMode === MULTI_VIEW_MODE;
  const targetMaxLength = isMultiView ? (isSmallScreen ? 8 : 9) : (isSmallScreen ? 7 : 8);
  
  const { formattedMinBet, formattedMaxBet } = formatMinMaxBetsForDisplay(minBet, maxBet, targetMaxLength);

  return (
    <div className={`${styles.minMaxContainer} min-max-bet`}>
      <BetIcon betIconUrl={betIconUrl} />
      <span className={styles.betText}>{`${formattedMinBet} - ${formattedMaxBet}`}</span>
    </div>
  );
};

export default MinMaxBet;
